import { useState, useEffect, Fragment } from "react";
import { useAuth } from "../../../../providers/auth";
import { Transition, Dialog } from "@headlessui/react";
import { Timestamp } from "firebase/firestore";

import { updateAutoSave, updateRenewHistory, updateStatusHistory } from "../../../../data/utility";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getJobStatusNoteNewSchema } from "../../../../schemas/JobStatusNoteNewSchema";
import { useGetCreditsByEmployer } from "../../../../data/employer";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../dashboard/AdminMetricDashboard/LoadingIndicator/LoadingSpinner";


const INITIAL_VALUES = {
    statusId: "live",
    closedType: "",
    closedFilledBy: "",
    closedReason: "",
};
interface StatusNoteNewProps {
    onDone: (message?: string, statusID?: string) => void;
    collection: string;
    id: string;
    jobData: any;
    name: string;
    daysLeft: number;
    placeholder?: string;
    status: string;
    required?: boolean;
    directions?: string;
    renewFromAdmin?: boolean;
    statusCollection?: any; // Update the type if you know what it is
    minLength?: number;
}

type FormValues = z.infer<ReturnType<typeof getJobStatusNoteNewSchema>>;

const RenewJobInAdvanceModal = ({
    onDone,
    collection,
    id,
    jobData,
    renewFromAdmin = false,
    name,
    daysLeft,
    status,
}: StatusNoteNewProps) => {

    const isEligibleForRenewal = ["closed_filled", "closed_cancelled", "closed_job_expired", "live"].includes(status) ? true : false
    let statusIdValue = "live";
    let closedTypeValue = "";
    const { user, role } = useAuth();
    const employerRole = role?.id;
    const navigate = useNavigate();
    const { data } = useGetCreditsByEmployer(jobData?.companyId)
    const [isInitialized, setIsInitialized] = useState(false);
    const [creditsAvailable, setCreditsAvailable] = useState([])
    const [openPublishModal, setOpenPublishModal] = useState(false)
    const [isPublishing, setIsPublishing] = useState(false);
    const [formSchema, setFormSchema] = useState(
        getJobStatusNoteNewSchema(false, false),
    );
    console.log("daysLeft", daysLeft)

    useEffect(() => {
        setCreditsAvailable(data)
    }, [data])
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(formSchema), // Initial schema
        defaultValues: INITIAL_VALUES,
    });

    const statusId = watch("statusId");
    const closedType = watch("closedType");
    const onSubmit = async () => {
        if (renewFromAdmin) {

            await updateAutoSave(collection, id, {
                [name]: statusIdValue,
                closedFilledBy: "",
                closedReason: "",
                // creditId: null,
                expirationDate: null,
                isPostedOrUpdatedFromAdmin: user?.claims?.type === "A" ? true : false,
                previouslyMadeLiveDate: jobData?.makeLiveDate.seconds ? jobData?.makeLiveDate : null,
                postedBy: {
                    userId: user?.id || "",
                    userName: user?.name || user?.displayName || "",
                },
                firstPublishedDate: null,
                ...{
                    // 👇 Keep your structure but apply future logic here
                    makeLiveDate: (() => {
                        const date = new Date();
                        if (daysLeft <= 0) return date;
                        // Example: +1 day
                        date.setDate(date.getDate() + daysLeft);
                        return date;
                    })(),
                },
            });


            const history = {
                alteredBy: user.id,
                effectiveDate: Timestamp.now(),
                from: status,
                status: statusIdValue,
            };
            const f_liveOrRenew = isEligibleForRenewal ? "renew_date" : "first_live_date"
            const renewHistory = {
                [f_liveOrRenew]: new Date(),
                postedBy: {
                    userId: user?.id || "",
                    userName: user?.name || user?.displayName || "",
                },

            };
            await updateStatusHistory(collection, id, history);
            await updateRenewHistory(collection, id, renewHistory)
            onDone("Status Updated", statusIdValue);
            setIsPublishing(false)
            window.location.reload();

        } else {
            setOpenPublishModal(true)
        }


    };


    const makeLiveJob = async () => {
        setIsPublishing(true);
        await updateAutoSave(collection, id, {
            [name]: statusIdValue,
            closedFilledBy: "",
            closedReason: "",
            creditId: null,
            expirationDate: null,
            isPostedOrUpdatedFromAdmin: user?.claims?.type === "A" ? true : false,
            previouslyMadeLiveDate: jobData?.makeLiveDate.seconds ? jobData?.makeLiveDate : null,
            postedBy: {
                userId: user?.id || "",
                userName: user?.name || user?.displayName || "",
            },
            firstPublishedDate: null,
            ...{
                // 👇 Keep your structure but apply future logic here
                makeLiveDate: (() => {
                    const date = new Date();
                    if (daysLeft <= 0) return date;
                    // Example: +1 day
                    date.setDate(date.getDate() + daysLeft);
                    return date;
                })(),
            },
        });


        const history = {
            alteredBy: user.id,
            effectiveDate: Timestamp.now(),
            from: status,
            status: statusIdValue,
        };
        const f_liveOrRenew = isEligibleForRenewal ? "renew_date" : "first_live_date"
        const renewHistory = {
            [f_liveOrRenew]: new Date(),
            postedBy: {
                userId: user?.id || "",
                userName: user?.name || user?.displayName || "",
            },

        };
        await updateStatusHistory(collection, id, history);
        await updateRenewHistory(collection, id, renewHistory)
        onDone("Status Updated", statusIdValue);
        setIsPublishing(false)
        window.location.reload();

    };
    useEffect(() => {
        if (status && !isInitialized) {
            setValue("statusId", status);
            setIsInitialized(true);
        }
    }, [status, isInitialized]);

    // Handle the statusId change and dynamically update the schema
    useEffect(() => {
        if (statusId === "closed") {
            const newSchema = getJobStatusNoteNewSchema(
                true,
                closedType === "closed_filled",
            );
            setFormSchema(newSchema);
            reset(
                {
                    ...INITIAL_VALUES,
                    statusId: "closed",
                    closedType,
                },
                { errors: true, dirtyFields: true },
            ); // Keep current values and reset other state
        } else {
            const newSchema = getJobStatusNoteNewSchema(false, false);
            setFormSchema(newSchema);
        }
    }, [statusId, closedType, reset]);

    const statusSpecificTheme = isEligibleForRenewal ? "bg-[#FAC7C7] " : "bg-[#FDE1AF] ";

    return (
        <>
            <div onClick={() => onSubmit()} >
                Renew Now
            </div>

            {openPublishModal && <Transition appear={true} show={true}>
                <Dialog
                    className="overflow-y-auto fixed inset-0 z-50"
                    onClose={() => null}
                >
                    <div className="px-4 min-h-screen text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75" />
                        </Transition.Child>
                        <span
                            className="inline-block h-screen align-middle"
                            aria-hidden="true"
                        >
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="inline-block overflow-hidden p-6 my-8  max-w-md text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
                                {!openPublishModal && <Dialog.Title className="flex flex-row justify-between items-center text-lg font-semibold leading-6 text-gray-900 mb-4">
                                    <span>{statusId === "closed" ? "Close Job" : !openPublishModal ? "Status" : ""}</span>
                                    <button
                                        className="w-[48px] h-[48px] rounded-full bg-[#F8F9FA] text-[#183852]"
                                        onClick={() => onDone()}
                                    >
                                        X
                                    </button>
                                </Dialog.Title>}

                                {statusId != "closed" && !openPublishModal && <hr />}


                                {
                                    openPublishModal && (
                                        <>
                                            {creditsAvailable?.length > 0 ? <>
                                                <div className="w-full flex flex-col justify-center items-center">
                                                    <div className=" w-full  flex justify-center items-center">

                                                        <img className="w-24 h-24 justify-center items-center" src="/images/publish_job.png">
                                                        </img>

                                                    </div>
                                                    <h1 className="text-2xl text-instant-teams-blue-Main font-semibold">{isEligibleForRenewal ? "Renew This Job!" : "Publish a Job"}</h1>
                                                    <div className="mt-4">
                                                        <p className="text-center text-sm font-thin text-gray-500"> {"You’re staying ahead by renewing this posting before it expires"} </p>
                                                    </div>
                                                    <div className="w-full   mb-8">
                                                        <h1 className="text-lg mt-4  text-instant-teams-blue-Main text-lg font-semibold"> Renewing will:</h1>

                                                        <p className="text-sm  ml-[5%] text-instant-teams-blue-Main"> {"- Use 1 credit from your balance"} </p>
                                                        <p className="text-sm ml-[5%] text-instant-teams-blue-Main "> {"- Add 30 more days to this posting"}</p>
                                                    </div>
                                                </div>

                                                <div className="w-full flex justify-between  " >
                                                    <button onClick={() => { setOpenPublishModal(false) }} className='border-[1px] space-x-3 border-instant-teams-blue-Main rounded-full py-2 px-12 '>
                                                        Cancel
                                                    </button>
                                                    <button onClick={makeLiveJob} className='bg-instant-teams-blue-Main w-40 rounded-full py-2 px-8 text-white items-center flex space-x-1 gap-1  justify-center'>
                                                        {isPublishing ? <div className={`items-center  flex space-x-4 gap-1  justify-center`}>
                                                           <LoadingSpinner size="5" borderColor="border-[#FFFFFF]" borderSize="2" />  </div>
                                                            : isEligibleForRenewal ? "Renew Job" : "Publish & Use 1 Credit"}
                                                    </button>

                                                </div>
                                            </> :
                                                (creditsAvailable?.length === 0 && employerRole === "PRI")
                                                    ? (<>

                                                        <div className="w-full flex flex-col justify-center items-center">
                                                            <div className=" w-full  flex justify-center items-center">

                                                                <img className="w-24 h-24 justify-center items-center" src="/images/red_notice.png">
                                                                </img>

                                                            </div>
                                                            <h1 className="text-xl text-instant-teams-blue-Main font-semibold">Publish a Job</h1>

                                                            <h1 className="text-lg my-4 mb-6 text-instant-teams-blue-Main text-center font-semibold">{"Insufficient credits to publish the job. Please purchase additional credits."}</h1>
                                                        </div>

                                                        <div className="w-full px-24 flex justify-Center items-center  " >
                                                            <button onClick={() => {
                                                                setOpenPublishModal(false); onDone()
                                                                navigate("/current-plan?buyMoreCredits=true");
                                                            }} className='border-[1px] w-full bg-instant-teams-blue-Main rounded-full py-4 text-[#FFFFFF]'>
                                                                Buy more credits
                                                            </button>


                                                        </div>
                                                    </>)
                                                    :
                                                    (creditsAvailable?.length === 0 && employerRole === "GEN")
                                                        ? (<>

                                                            <div className="w-full flex flex-col justify-center items-center">
                                                                <div className=" w-full  flex justify-center items-center">

                                                                    <img className="w-24 h-24 justify-center items-center" src="/images/red_notice.png">
                                                                    </img>

                                                                </div>
                                                                <h1 className="text-xl text-instant-teams-blue-Main font-semibold">Publish a Job</h1>

                                                                <h1 className="text-lg my-4 mb-6 text-instant-teams-blue-Main text-center font-semibold">{"Insufficient credits to publish the job. Please contact Account Owner."}</h1>
                                                            </div>

                                                            <div className="w-full px-24 flex justify-Center items-center  " >
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenPublishModal(false);
                                                                        onDone();
                                                                    }} className='border-[1px] w-full bg-instant-teams-blue-Main rounded-full py-4 text-[#FFFFFF]'
                                                                >
                                                                    Okay
                                                                </button>


                                                            </div>
                                                        </>)
                                                        : (<></>)
                                            }
                                        </>
                                    )
                                }
                            </div>
                        </Transition.Child>
                    </div>


                </Dialog>
            </Transition>}


        </>

    );
};

export default RenewJobInAdvanceModal;
